import { NextRequest, NextResponse } from 'next/server'
import { and, eq } from 'drizzle-orm'
import { db } from '@/db'
import { auditLog, tickets } from '@/db/schema'
import { requireExpertApiUser } from '@/lib/role-guard'
import { InvalidTransitionError, transition, type TicketStatus } from '@/lib/ticket-state-machine'

export async function PATCH(
  request: NextRequest,
  { params }: { params: Promise<{ ticketId: string }> },
) {
  const guard = await requireExpertApiUser()
  if (!guard.ok) return guard.response

  const { ticketId } = await params
  const body = await request.json().catch(() => null)

  if (!body?.status || typeof body.status !== 'string') {
    return NextResponse.json({ error: 'status is required' }, { status: 400 })
  }
  if (typeof body.version !== 'number') {
    return NextResponse.json({ error: 'version is required' }, { status: 400 })
  }

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, ticketId), eq(tickets.tenantId, guard.user.tenantId)))
    .limit(1)

  if (!ticket) return NextResponse.json({ error: 'Ticket not found' }, { status: 404 })

  let nextStatus: TicketStatus
  try {
    nextStatus = transition(ticket.status, body.status as TicketStatus)
  } catch (err) {
    if (err instanceof InvalidTransitionError) {
      return NextResponse.json({ error: err.message }, { status: 409 })
    }
    throw err
  }

  const [updated] = await db
    .update(tickets)
    .set({ status: nextStatus, version: body.version + 1, updatedAt: new Date() })
    .where(and(eq(tickets.id, ticketId), eq(tickets.version, body.version)))
    .returning()

  if (!updated) return NextResponse.json({ error: 'Concurrent update — retry' }, { status: 409 })

  await db.insert(auditLog).values({
    tenantId: guard.user.tenantId,
    actorId: guard.user.id,
    actorType: 'user',
    action: 'ticket.status_changed',
    entityType: 'ticket',
    entityId: ticketId,
    payload: { from: ticket.status, to: nextStatus },
  })

  return NextResponse.json({ ticketId, status: updated.status, version: updated.version })
}
